<template>
  <div class="editor">
    <vue-editor
      :id="'notificationEditor-'+uniqid"
      ref="editor"
      v-model="content"
      :editor-options="editorSettings"
      @selection-change="selectionChange"
      @editor-change="onEditorChange"
      @blur="isFocused = false"
      @focus="isFocused = true"
      @ready="onEditorReady"
    />
    <div class="toolbar__container">
      <div
        id="notification-editor-toolbar"
        class="d-flex align-items-center"
      >
        <!-- default at 12pt -->
        <span class="ql-formats">
          <button
            v-if="attachments && useFeature('upload_file')"
            type="button"
            class="ql-attach"
            @click="triggerAddAttachment"
          >
            <editor-attach-file
              v-show="false"
              v-if="useFeature('upload_file')"
              ref="attachFile"
              :show="addAttachment"
              :clear="clearAttachment"
              :value="attachmentName"
              :multiple="false"
              accept="application/pdf, image/png, image/jpeg, image/gif"
              @input="$emit('upload-attachment', $event)"
              @upload-started="onUploadStarted"
              @progress="attachmentProgress = $event"
              @uploaded="onUploadSuccess"
              @cleaned="onUploadCleaned"
              @error="onUploadFailure"
            />
          </button>
          <button
            type="button"
            class="ql-link"
          />
          <button
            type="button"
            class="ql-bold"
          />
          <button
            type="button"
            class="ql-italic"
          />
          <button
            v-if="emojis"
            type="button"
            class="ql-emoji"
            @click="toggleEmojiDialog"
          />
          <!--  <button type="button"
            class="ql-underline" /> -->
        </span>
        <span class="ql-formats mr-0 ml-auto">
          <base-text
            size="xs"
            class="text-right"
            light
          >
            <span
              class="cursor-pointer pr-2"
              style="border-right: dashed 1px #33333388;"
              @click="$emit('cancel')"
            >Close</span>
            <span
              class="cursor-pointer pl-2"
              @click="post"
            >
              Post
            </span>
          </base-text>
        </span>
        <div
          v-show="!dialogHidden"
          class="picker"
        >
          <v-emoji-picker
            :emoji-size="27"
            :emoji-with-border="false"
            :emojis-by-row="10"
            @select="onSelectEmoji"
          />
        </div>
        <div
          v-if="attachmentName"
          class="px-2 pt-3"
        >
          <div class="d-flex align-items-center">
            <base-text size="s">
              <base-icon
                icon="cancel"
                @click="triggerClearAttachment"
              /> {{ attachmentName }}
            </base-text>
          </div>
        </div>
      </div>
    </div>
    <attachments
      class="pt-4"
      :files="attachedFiles"
      @clear="triggerClearAttachment"
    />
  </div>
</template>
<script>
import { VueEditor } from 'vue2-editor';
import VueEditorMixins from '@/_utilities/VueEditorMixins';
import { VEmojiPicker } from 'v-emoji-picker';
import EditorAttachFile from '@/pages/entries/EditorAttachFile';
import Attachments from '@/components/Widgets/Notifications/Attachments';

const [IDLE, UPLOADING, DONE] = [...Array(3).keys()];

export default {
  components: {
    VueEditor, EditorAttachFile, Attachments, VEmojiPicker,
  },
  mixins: [VueEditorMixins],
  props: {
    attachments: {
      type: Boolean,
      default: false,
    },
    emojis: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    content: '',
    cleanContent: '',
    uniqid: Date.now(),
    addAttachment: false,
    clearAttachment: false,
    attachmentName: '',
    attachmentUrl: null,
    attachmentTempName: '',
    attachmentProgress: 0,

    dialogHidden: true,
    uploadStatus: IDLE,

    editorSettings: {
      formats: [
        'background',
        'bold',
        'color',
        'font',
        'code',
        'italic',
        'link',
        'size',
        'strike',
        // 'script',
        'underline',
        'blockquote',
        'header',
        'indent',
        'list',
        'align',
        'direction',
        'code-block',
        'font',
        'hashtag',
        // 'image'
        // 'video'
      ],
      modules: {
        toolbar: // ['bold', 'emoji'],
        {
          container: '#notification-editor-toolbar',
          handlers: {
            addTag: () => {
              this.addCustomTag('Name');
            },
          },
        },
      },
    },
  }),
  computed: {
    attachedFiles() {
      const name = this.uploadStatus === UPLOADING ? this.attachmentTempName
        : this.attachmentName || this.attachmentTempName;
      const progress = this.attachmentProgress;
      return !name || !name.length ? []
        : [{
          name,
          progress,
        }];
    },
  },
  mounted() {
    this.$nextTick().then(() => {
      if (this.$refs.editor) {
        this.$refs.editor.quill.focus();
      }
    });
  },
  methods: {
    post() {
      this.$emit('post', { message: this.cleanContent, attachment: this.attachmentUrl });
    },
    toggleEmojiDialog() {
      this.dialogHidden = !this.dialogHidden;
    },
    onSelectEmoji(emojiData) {
      const length = this.$refs.editor.quill.getLength();
      const position = this.$refs.editor.quill.getSelection()?.index ?? length - 1;
      this.$refs.editor.quill.insertText(position, ` ${emojiData.data}`);
      this.toggleEmojiDialog();
    },
    triggerAddAttachment() {
      this.addAttachment = true;
      window.setTimeout(() => {
        this.addAttachment = false;
      }, 200);
    },
    triggerClearAttachment() {
      this.clearAttachment = true;
      window.setTimeout(() => {
        this.clearAttachment = false;
      }, 200);
    },
    onUploadStarted(filename) {
      this.attachmentTempName = filename;
      this.attachmentProgress = 0;
      this.uploadStatus = UPLOADING;
    },
    onUploadSuccess(url) {
      this.attachmentUrl = url;
      this.attachmentProgress = null;
      this.uploadStatus = DONE;
    },
    onUploadFailure(error) {
      this.$emit('attachment-error', error);
      this.attachmentTempName = '';
      this.attachmentProgress = null;
      this.uploadStatus = IDLE;
    },
    /** Called after successfully resetting attachment to nothing */
    onUploadCleaned() {
      this.attachmentTempName = '';
      this.attachmentProgress = null;
      this.attachmentName = '';
      this.attachmentUrl = null;
      this.uploadStatus = IDLE;
    },
  },
};
</script>
<style lang="scss"
  scoped>
::v-deep button[class^="ql-"][type="button"]:not(.ql-addTag)  {
  margin-right: 3px !important;
  padding-bottom: 18px !important;
  padding-right: 17px !important;
  &.ql-active {
    background-color: rgba(black, 0.1) !important;
    border-radius: 4px !important;
  }
}
.editor {
  margin-bottom: 2.5rem;

  &__text {
    width: 100%;
    appearance: none;
    border-radius: 3px;
    border: solid 1px #ccc !important;
    height: 50px;
  }
}
::v-deep .ql-editor {
  min-height: 70px !important;
  max-height: 200px;
  color: $black !important;
  font-family: $font-family-sans-serif-light;
  font-size: 13px;
}
::v-deep .ql-editor strong{
  font-family: $font-family-sans-serif-medium;
  font-weight: normal;
}
::v-deep .toolbar__container {
  margin: unset !important;
  padding-top: 0;
}
::v-deep .ql-toolbar.ql-snow {
  border: unset !important;
  border-bottom: solid 1px rgba($black, 0.25) !important;
  border-left: solid 1px rgba($black, 0.25) !important;
  border-right: solid 1px rgba($black, 0.25) !important;
}
::v-deep .ql-toolbar {
  bottom: 8px !important;
}
::v-deep .quillWrapper {
  border-radius: 3px;
  border: solid 1px rgba($black, 0.25) !important;
  border-bottom: 0 !important;
  margin: unset !important;
  // overflow: hidden;
}
::v-deep .ql-emoji {
  background-image: url("~@/assets/icons/emoji.svg") !important;
}

#notification-editor-toolbar {
  .ql-formats{
    margin-right: 10px;
  }
  .ql-formats button {
    width: 20px;
    height: 16px;
  }
}
::v-deep .ql-tooltip {
  z-index: 20;
}
::v-deep .base-progress-bar {
  width: 50%;
}

.picker {
  position: absolute;
  left: 235px;
  top: -63px;
}
</style>
